import React, {
  useState, useContext, useEffect, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FLAGS } from '../platform/config/flags';
import { isCustomerTypeAllowed, isSurfaceEnabled } from '../platform/config/surface';
import { emit, subscribe } from '../platform/events/lifecycle';
import { AssistantActionButton } from '../shared/fab/AssistantActionButton';
import { LazyAssistantDataProvider } from './dynamic-import';
import { LazyLegacyApp } from '../legacy/dynamic-import';
import { LazyAssistantShell } from '../shell/dynamic-import';
import { useDrawerController } from '../shell/useDrawerController';
import { DrawerControlContext } from '../shell/DrawerControlContext';
import { setLoggingEnabled } from '../shared/utils/logger';
import { getIsNewCompatiblePage, getIsLegacyCompatiblePage } from '../shared/utils/assistant-utils';
// Renderer-free manifest import (see journeys/registry.js): safe for the eager main
// chunk — it does NOT pull the assistant runtime.
import { resolveJourneyId, DEFAULT_JOURNEY_ID } from '../journeys/registry';
import { useStickyElementOffset } from '../hooks/useStickyElementOffset';
import {
  HEADER_ENTRY, MAGIC_APRON_CONTAINER_ID, UPDATE_DRAWER_STATE,
} from '../shared/assistant-config';
import { buildAnalyticsQuestion } from '../shared/analytics';
import './app.scss';

// App is the eager bootstrap/composition root: it decides whether the assistant runs on
// this surface, which journey is active, and wires the host-event bus — then renders the
// FAB + the deferred assistant tree (the data provider is kept mounted while the drawer
// is closed so queries warm ahead of open, wrapping the lazy Shell rendered once the
// drawer opens). The drawer state machine + FAB behavior live in the Shell's eager
// controller (useDrawerController, WS-10.8); App holds no drawer or session state. Both lazy
// boundaries retry their own chunk import (see createLazyComponent), so no retry wrapper
// lives here.
//
// It also chooses between the two CONTENT TREES: the consolidated in-repo tree (the data
// provider + Shell below) and the legacy package tree (src/legacy/). This is not master's
// live-chat-unification switch returning — that one picked between two npm builds of the
// component, and both trees now live here. What it shares with it is the reason it exists at
// all: one decision, in one place, so retiring the loser is a directory delete.

// If the lazy provider subtree throws during render — e.g. the intermittent "invalid
// hook call" when the data-provider chunk resolves against a duplicate React — it must
// be caught by a boundary that is a PARENT of it (a boundary never catches its own
// render errors). Logging it here gives a triage signal instead of a blank assistant
// and keeps the FAB alive. This catches render/commit errors only; an async chunk-load
// failure is surfaced inside createLazyComponent (rethrowOnError) to this same boundary.
const logProviderError = ({ error, name }) => {
  // eslint-disable-next-line no-console
  console.error(`[magic-apron-assistant] "${name}" boundary caught an error:`, error);
};

export const App = ({ activateStyles }) => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  const { lesser: isMobile } = useBreakpoint('lg');
  // The active journey the host page selected via the set-journey lifecycle event (WS-6).
  // Threaded down to the shell's JourneyProvider; defaults to the eager core.
  const [journeyId, setJourneyId] = useState(DEFAULT_JOURNEY_ID);
  const offsetMargin = useStickyElementOffset();

  const fsDTEnabled = useConfigService(FLAGS.assistantDtEnabled);
  const fsMWEnabled = useConfigService(FLAGS.assistantMwEnabled);
  const fsAPPEnabled = useConfigService(FLAGS.assistantAppEnabled);
  const fsRenderOnB2B = useConfigService(FLAGS.assistantB2bEnabled) ?? false;
  // Opts INTO the consolidated tree. Legacy is the default, so anything that is not an
  // explicit true — unset, still resolving, or false — means legacy.
  const fsUnifiedEnabled = useConfigService(FLAGS.assistantUnifiedEnabled);
  const isValidCustomerType = isCustomerTypeAllowed(customer?.type, fsRenderOnB2B);
  // Page eligibility (MAG4-815, merged from master). Master read this inside `AppSwitch` and
  // gated it on `isLiveChatUnificationEnabled && isExclusionRulesEnabled`, coupling the rules
  // to the tree it chose. Deliberately NOT coupled to `assistantUnifiedEnabled` here, for two
  // reasons: which pages the assistant may run on is loader chrome and package-agnostic, and
  // eligibility is decided at MOUNT while the tree is not settled until later — so a
  // tree-coupled gate would read a flag that has not resolved yet and silently disable the
  // rules. Gated on the rules flag alone; when it is off, the legacy allow-list applies
  // exactly as before, for both trees.
  const isExclusionRulesEnabled = useConfigService(FLAGS.exclusionRulesEnabled);
  const excludedPagesConfig = useConfigService(FLAGS.excludedPages);
  const isCompatiblePage = isExclusionRulesEnabled
    ? getIsNewCompatiblePage(excludedPagesConfig)
    : getIsLegacyCompatiblePage();

  useEffect(() => {
    if (isCompatiblePage && activateStyles) {
      activateStyles();
    }
  }, [isCompatiblePage]);

  const isLoggingEnabled = useConfigService('enable-maa-lc-console-logs');

  useEffect(() => {
    setLoggingEnabled(!!isLoggingEnabled);
  }, [isLoggingEnabled]);

  const {
    drawerState,
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  } = useDrawerController();

  const openDrawerRef = useRef(openDrawer);
  openDrawerRef.current = openDrawer;

  // Has the drawer EVER been open this page-load? A latch, not a mirror of drawerState (Seq
  // G.3): the GraphQL data layer mounts the first time the shopper engages and then stays
  // mounted, so a close-then-reopen never rebuilds the Apollo client or re-warms the queries.
  // Most PDP loads never open the drawer at all, and those pay nothing for the data layer.
  const hasEverOpenedRef = useRef(false);
  if (drawerState !== 'CLOSED') hasEverOpenedRef.current = true;
  const withDataLayer = hasEverOpenedRef.current;

  // Which content tree renders. Legacy is the default — only an explicit `true` selects the
  // consolidated tree, so an unset flag renders legacy rather than nothing.
  //
  // Frozen on the first DEFINED value of the flag, NOT at first drawer-open. Keying the
  // freeze on drawer-open is wrong, and wrong in the direction that silently corrupts an
  // experiment: `useDrawerController` auto-opens a returning session from its MOUNT effect
  // (and the host page can open the drawer over the bus just as early), while
  // `useConfigService` seeds from context and then merges later CONFIG_SERVICE_CHANGE
  // payloads — so the drawer is routinely open before the flag has arrived. A drawer-open
  // freeze reads `undefined`, commits `legacy`, and a shopper who IS in the consolidated
  // group stays on legacy for the whole page load with nothing failing.
  //
  // Freezing on the first defined value instead honours whenever the flag lands, and still
  // ignores any LATER flip — the property the freeze exists for, since a config change must
  // not tear down a live conversation.
  //
  // ⚠ There is no "resolved" signal to wait for: useConfigService returns undefined both for
  // a key that has not arrived yet and for one that is not configured at all, so treating
  // undefined as "wait" would render nothing forever on the production default.
  //
  // In practice the flag lands in the SAME payload as the surface flags below, which App
  // needs before it renders anything at all — so the common path mounts the right tree first
  // time. Only a config service that splits them across payloads can mount legacy and then
  // swap, which is a visible teardown but the correct experience to end on.
  const observedUnifiedRef = useRef(null);
  if (observedUnifiedRef.current === null && fsUnifiedEnabled !== undefined) {
    observedUnifiedRef.current = fsUnifiedEnabled === true;
  }
  const contentTree = observedUnifiedRef.current === true ? 'unified' : 'legacy';

  // Drawer commands handed DOWN to the assistant subtree, replacing the descendant→ancestor
  // MAA_OPEN_DRAWER broadcast hop. Memoized on `openDrawer`'s behavior rather than identity:
  // the controller rebuilds its handlers every render, so an unmemoized object here would
  // give every consumer a new context value on each App render. `openDrawer` closes over
  // refs and state setters only, so a stable wrapper is safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const drawerControl = useMemo(() => ({ openDrawer: () => openDrawerRef.current() }), []);

  const handleMagicApronEvents = ({ eventName, output }) => {
    // Entry-point journey selection: the host page fires
    // `magic-apron-assistant.set-journey` with { journeyId } to route the assistant into
    // a specific journey. Normalized against the registry so an unknown/stale hint falls
    // back to the default journey.
    if (eventName === 'set-journey') {
      setJourneyId(resolveJourneyId(output?.journeyId));
    }
    if (eventName === 'question-ask') {
      const {
        messageOrigin,
        pageType,
        entryLocationForAnalytics,
        userQuestion,
        sessionID,
        actionID
      } = output;
      markShouldAutoOpen();
      emit(HEADER_ENTRY.SUBMIT_EVENT, {
        sessionID,
        question: buildAnalyticsQuestion({
          entryLocation: entryLocationForAnalytics,
          pageType,
          messageOrigin,
          userQuestion,
        }),
        actionID,
      });
    }
  };

  useEffect(() => {
    const unsubscribeEvents = subscribe('magic-apron-assistant.*', handleMagicApronEvents);
    return () => {
      unsubscribeEvents();
    };
  }, []);

  // scoped to the unification cohort
  const isHeaderEntryEligible = Boolean(
    contentTree === 'unified'
    && isCompatiblePage
    && isValidCustomerType
    && !isMobile
    && fsDTEnabled
  );

  // Above the `isCompatiblePage` early return on purpose: an excluded page still has to say
  // `available: false`.
  useEffect(() => {
    emit(
      HEADER_ENTRY.STATUS_EVENT,
      { available: isHeaderEntryEligible, drawerState },
      { channel: 'lifeCycle' },
    );
  }, [isHeaderEntryEligible, drawerState]);

  const handleHeaderRequestRef = useRef();
  handleHeaderRequestRef.current = (requestedDrawerState) => {
    if (requestedDrawerState === 'OPEN') handleOpen(true, HEADER_ENTRY.ORIGIN_HEADER);
    if (requestedDrawerState === 'CLOSED') handleClose(HEADER_ENTRY.ORIGIN_HEADER);
  };

  // Requests are absolute, so the bus replaying them — including on StrictMode's second
  // mount — is a no-op rather than a flip.
  useEffect(() => {
    const unsubscribeHeaderEntry = subscribe(
      UPDATE_DRAWER_STATE,
      ({ output } = {}) => handleHeaderRequestRef.current(output?.drawerState),
      { channel: 'lifeCycle' },
    );
    return () => {
      unsubscribeHeaderEntry();
    };
  }, []);

  if (!isSurfaceEnabled({
    isMobile,
    isConsumerApp,
    dtEnabled: fsDTEnabled,
    mwEnabled: fsMWEnabled,
    appEnabled: fsAPPEnabled,
  // Each of these three is independently disqualifying, so they are OR-ed. Written as
  // `|| !isValidCustomerType && !isCompatiblePage` this would parse as
  // `|| (!isValidCustomerType && !isCompatiblePage)` — `&&` binds tighter — which both
  // weakened the B2B gate (an invalid customer type alone no longer bailed) and made the
  // exclusion rules a no-op for every valid customer.
  }) || !isValidCustomerType || !isCompatiblePage
  ) return null;

  // FAB + open/close state work before the lazy Shell has loaded; the Shell renders from
  // these props. isMobile is NOT passed — the Shell derives it from useBreakpoint('lg')
  // internally (WS-10.5).
  const drawerProps = {
    drawerState,
    offsetMargin,
    journeyId,
    handleClose,
    handleDrawerStateChange,
  };

  return (
    <>
      {(drawerState === 'CLOSED') && (
        <ImpressionProvider
          data={{
            id: MAGIC_APRON_CONTAINER_ID,
            component: 'MagicApronAssistant',
            name: 'MagicApronLoader',
            type: 'content'
          }}
        >
          <AssistantActionButton
            offsetMargin={offsetMargin}
            onClick={() => handleOpen(true)}
            isUnifiedExperience={contentTree === 'unified'}
          />
        </ImpressionProvider>
      )}
      <ErrorBoundary name="magic-apron-assistant-dynamic-provider" onError={logProviderError}>
        {/* The drawer-control context wraps BOTH trees. Legacy has no consumer for it — the
            consumers are the orchestrator and the Assistant journey — but its default is an
            inert no-op, so leaving it above the switch costs nothing and keeps the branch
            below down to the one thing that actually differs. */}
        <DrawerControlContext.Provider value={drawerControl}>
          {contentTree === 'legacy' ? (
            // Nothing while closed, by design: the legacy package has no warm-while-closed
            // story, so its chunk is not fetched until the shopper opens the drawer. Props
            // are listed rather than spread — `drawerProps` carries `journeyId`, which is a
            // consolidated-tree concept this tree has no notion of.
            (drawerState !== 'CLOSED') && (
              <LazyLegacyApp
                drawerState={drawerState}
                handleDrawerStateChange={handleDrawerStateChange}
                offsetMargin={offsetMargin}
                handleClose={handleClose}
              />
            )
          ) : (
            /* journeyId goes to BOTH halves: the provider needs it above the orchestrator
               to inject the active journey's flows/config (Seq F.c.0), the Shell needs it
               below for JourneyProvider (render-side renderers + content). The provider is
               mounted while CLOSED — that is the warm path. */
            <LazyAssistantDataProvider journeyId={journeyId} withDataLayer={withDataLayer}>
              {(drawerState !== 'CLOSED') && (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <LazyAssistantShell {...drawerProps} />
              )}
            </LazyAssistantDataProvider>
          )}
        </DrawerControlContext.Provider>
      </ErrorBoundary>
    </>
  );
};

App.propTypes = {
  activateStyles: PropTypes.func,
};
